import {
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';

import {
  ControlContainer,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FloatingInputComponent } from '../floating-input/floating-input.component';
import { TagsComponent } from '../tags/tags.component';

@Component({
  selector: 'app-tag-input',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatTooltipModule,
    FloatingInputComponent,
    TagsComponent
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagInputComponent),
      multi: true,
    },
  ],
  templateUrl: './tag-input.component.html',
  styleUrls: ['./tag-input.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class TagInputComponent implements ControlValueAccessor, OnInit {
  private injector = inject(Injector);

  label = input('');
  toolTip = input(
    'Insert a tag and press ENTER to add it. You can add multiple tags but their name should be unique.',
  );

  required = input(false);
  name = input('tags');

  maxlength = input<string | number>(null);
  minlength = input<string | number>(null);

  disabled = signal(false);
  tags = signal<string[]>([]);

  protected ngControl = signal<NgControl | null>(null);

  // These are placeholder functions that will be replaced by registerOnChange/registerOnTouched
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string[]) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnInit() {
    this.ngControl.set(this.injector.get(NgControl, null));
  }

  textModel = model('');
  onTextModelChange(modelVal: string) {
    this.textModel.set(modelVal);

    // Validation for duplicate tags
    this.checkDuplicateTags();
  }

  enterPressed(event: KeyboardEvent) {
    // Prevent form submission when Enter is pressed in the tag input
    event.preventDefault();
    event.stopPropagation();

    const newTag = this.textModel().trim();
    if (!newTag || !this.ngControl()?.control.valid) {
      return;
    }

    const newTags = [...this.tags(), newTag];
    this.textModel.set('');

    this.tags.set(newTags);

    // Notify the form that the value has changed
    // Don't set tags directly - let Angular update it via writeValue
    this.onTouched();
    this.onChange(newTags);
  }

  removeTag(index: number) {
    const currentTags = this.tags();
    const newTags = currentTags.filter((_, i) => i !== index);
    this.tags.set(newTags);

    this.checkDuplicateTags();

    // Notify the form that the value has changed
    this.onTouched();
    this.onChange(this.tags());
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(value: string[] | null): void {
    if (value) {
      this.tags.set(value || []);
      console.log('writeValue called with', value, this.tags(), this.textModel());
    }
  }

  private checkDuplicateTags() {
    if (this.ngControl()?.control) {
      const lowerCaseModel = this.tags().map((tag) => tag?.toLowerCase());
      const lowerCaseInput = this.textModel().toLowerCase();
      if (lowerCaseModel.includes(lowerCaseInput)) {
        this.ngControl().control.setErrors({ duplicateTag: true });
      } else {
        this.ngControl().control.setErrors(null);
      }
    }
  }
}
